import { DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InputNumber, Select, Spin, message } from 'antd';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageTitle from 'apps/home/components/PageTitle';
import stockApiCall from 'apps/investment/apiCalls/stock';
import {
  getTargetPortfolioApiCall,
  setTargetPortfolioApiCall,
} from 'apps/investment/apiCalls/portfolio-target';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useFetchApiList } from 'hooks/api';

const TOTAL_TOLERANCE = 0.1;
const debounceTimeout = 500;

export const PortfolioTargetPage = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [getTargetPortfolioApiCall.queryKey, user_id],
    queryFn: () => getTargetPortfolioApiCall.queryFn({ user_id }),
  });

  const [rows, setRows] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (response?.data && !initialized) {
      setRows(
        (response.data.items || []).map(item => ({
          stock_id: item.stock_id,
          symbol: item.symbol,
          name: item.name,
          target_percentage: item.target_percentage,
        }))
      );
      setInitialized(true);
    }
  }, [response, initialized]);

  const {
    data: stocks = [],
    load: loadStocks,
    isLoading: isLoadingStocks,
  } = useFetchApiList(stockApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    loadStocks({ page_size: 20 });
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(value => loadStocks({ search: value, page_size: 20 }), debounceTimeout),
    [loadStocks]
  );

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: () =>
      setTargetPortfolioApiCall.queryFn({
        user_id,
        items: rows.map(r => ({ stock_id: r.stock_id, target_percentage: r.target_percentage })),
      }),
    onSuccess: () => {
      message.success('Đã lưu danh mục mục tiêu');
      queryClient.invalidateQueries({ queryKey: [getTargetPortfolioApiCall.queryKey, user_id] });
      navigate(`/investment/portfolios/${user_id}/analysis`);
    },
    onError: err => {
      message.error(err?.message || 'Lưu danh mục mục tiêu thất bại');
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const existingStockIds = rows.map(r => r.stock_id);
  const availableStocks = stocks.filter(s => !existingStockIds.includes(s.id));
  const total = rows.reduce((sum, r) => sum + (r.target_percentage || 0), 0);
  const isTotalValid = Math.abs(total - 100) <= TOTAL_TOLERANCE;
  const hasInvalidRow = rows.some(r => !r.target_percentage || r.target_percentage <= 0);
  const canSave = rows.length > 0 && isTotalValid && !hasInvalidRow;

  const addStock = stockId => {
    const stock = stocks.find(s => s.id === stockId);
    if (!stock) return;
    setRows(prev => [
      ...prev,
      { stock_id: stock.id, symbol: stock.symbol, name: stock.name, target_percentage: 0 },
    ]);
  };

  const updateTarget = (stockId, value) => {
    setRows(prev =>
      prev.map(r => (r.stock_id === stockId ? { ...r, target_percentage: value || 0 } : r))
    );
  };

  const removeRow = stockId => {
    setRows(prev => prev.filter(r => r.stock_id !== stockId));
  };

  return (
    <>
      <PageTitle
        title="Danh mục mục tiêu"
        backUrl={`/investment/portfolios/${user_id}/analysis`}
      />

      <div className="my-4 flex items-center gap-2">
        <Select
          showSearch
          allowClear
          value={null}
          placeholder="Tìm mã cổ phiếu để thêm"
          style={{ width: 320 }}
          filterOption={false}
          onSearch={debouncedSearch}
          onChange={addStock}
          notFoundContent={isLoadingStocks ? <Spin size="small" /> : null}
          options={availableStocks.map(s => ({
            value: s.id,
            label: `${s.symbol} - ${s.name}`,
          }))}
        />
      </div>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-gray-500">
          Chưa có mã cổ phiếu mục tiêu nào. Thêm mã cổ phiếu để bắt đầu.
        </p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b text-left text-sm text-gray-500">
              <th className="py-2">Mã CK</th>
              <th className="py-2">Tên</th>
              <th className="py-2 text-right">Tỷ trọng mục tiêu (%)</th>
              <th className="py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.stock_id} className="border-b">
                <td className="py-2 font-medium">{row.symbol}</td>
                <td className="py-2 text-gray-500">{row.name}</td>
                <td className="py-2 text-right">
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    precision={2}
                    value={row.target_percentage}
                    onChange={value => updateTarget(row.stock_id, value)}
                    addonAfter="%"
                    style={{ width: 140 }}
                  />
                </td>
                <td className="py-2 text-center">
                  <DeleteOutlined
                    style={{ color: '#ff4d4f', cursor: 'pointer' }}
                    onClick={() => removeRow(row.stock_id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="my-4 flex items-center justify-between rounded-md border px-4 py-3">
        <span className="text-gray-500">Tổng cộng</span>
        <span className={isTotalValid ? 'font-semibold text-green-600' : 'font-semibold text-red-500'}>
          {total.toFixed(2)}%
        </span>
      </div>

      <Button type="primary" block disabled={!canSave} loading={isSaving} onClick={() => save()}>
        Lưu danh mục mục tiêu
      </Button>
    </>
  );
};
